<template>
  <!-- Where this host sits, for the fallback host to ask whether it is on screen. -->
  <span ref="anchor" hidden></span>
  <div
    v-if="visible"
    class="flex flex-wrap fixed max-w-[1228px] w-[93%] sm:w-full left-[52.5%] transform translate-x-[-52.5%] rounded-[8px] p-[12px] text-white text-[1rem] leading-[1.5rem]"
    :class="[color, safeArea ? '' : position]"
    :style="computedStyle"
  >
    <img v-if="icon" :src="icon" alt="" class="mr-[8px] shrink-0" />
    <div v-html="message" class="break-words min-w-0" @click="handleClick"></div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { mapState } from 'vuex';
import { isPlatform } from '@ionic/vue';
import { openCrispChat } from '@/utils/crisp';

// Page-level hosts currently mounted. The Set itself is not reactive; the
// counter is, and bumps whenever the answer to "is a page host on screen?" may
// have changed — a host mounting or unmounting (the member taps back
// mid-toast), or Ionic hiding or showing one without unmounting it (navigating
// forward, an overlay closing). The fallback host re-checks on every bump.
const pageHosts = new Set();
const pageHostsChanged = ref(0);
const bumpPageHosts = () => {
  pageHostsChanged.value += 1;
};

// Ionic keeps earlier pages and closed overlays mounted, just hidden.
const HIDDEN_ANCESTOR = '.ion-page-hidden, .overlay-hidden, [aria-hidden="true"]';

export default {
  props: {
    position: {
      type: String,
      default: 'bottom-[50px]',
    },
    safeArea: {
      type: Boolean,
      default: false,
    },
    zIndex: {
      type: Number,
      default: 10,
    },
    // The app-wide host in App.vue. Most pages mount no <Toast>, and messages
    // dispatched from store modules land on whatever page is open, so this one
    // shows them — unless a page-level host is on screen to do it instead.
    fallback: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('toast', ['message', 'icon', 'color']),
    visible() {
      if (!this.message) return false;
      if (!this.fallback) return true;
      // Read for reactivity only.
      // eslint-disable-next-line no-unused-expressions
      pageHostsChanged.value;
      return ![...pageHosts].some(host => host.isOnScreen());
    },
    computedStyle() {
      return this.safeArea
        ? { zIndex: this.zIndex, top: 'env(safe-area-inset-top)' }
        : { zIndex: this.zIndex };
    },
  },
  watch: {
    // Ionic hides and shows pages by class and aria-hidden, not by unmounting,
    // so the fallback watches those attributes — only while a message is up.
    message(message) {
      if (!this.fallback) return;
      if (message) this.watchVisibility();
      else this.stopWatchingVisibility();
    },
  },
  mounted() {
    if (this.fallback) return;
    pageHosts.add(this);
    bumpPageHosts();
  },
  beforeUnmount() {
    this.stopWatchingVisibility();
    if (pageHosts.delete(this)) bumpPageHosts();
  },
  methods: {
    watchVisibility() {
      if (this.visibilityObserver || typeof MutationObserver === 'undefined') return;
      this.visibilityObserver = new MutationObserver(bumpPageHosts);
      this.visibilityObserver.observe(document.body, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'aria-hidden'],
      });
    },
    stopWatchingVisibility() {
      this.visibilityObserver?.disconnect();
      this.visibilityObserver = null;
    },
    isOnScreen() {
      const { anchor } = this.$refs;
      return Boolean(anchor?.isConnected) && !anchor.closest(HIDDEN_ANCESTOR);
    },
    handleClick(event) {
      const link = event.target.closest('a');
      if (link?.dataset.action === 'open-crisp') {
        event.preventDefault();
        event.stopPropagation();
        this.openCrispChat();
      }
    },
    openCrispChat() {
      const isNative = (isPlatform('android') || isPlatform('ios')) && !isPlatform('mobileweb');

      if (isNative) {
        setTimeout(() => openCrispChat({ showFirst: true }), 250);
      } else {
        openCrispChat({ showFirst: true });
      }
    },
  },
};
</script>
